import { useCallback, useEffect, useRef, useState } from 'react'

// Ellipsis truncation is CSS-driven, so whether the tooltip is needed can only be
// measured. The measured box changes width with the sidebar and generate-panel
// toggles, not only with the viewport, so this observes the node itself rather than
// listening for window resizes.
export default function useIsTextTruncated(value) {
  const nodeRef = useRef(null)
  const observerRef = useRef(null)
  const [isTruncated, setIsTruncated] = useState(false)

  const measure = useCallback(() => {
    const node = nodeRef.current
    if (!node) return

    // Sub-pixel text metrics make scrollWidth exceed clientWidth by a fraction on
    // untruncated text, so allow 1px of slack.
    setIsTruncated(node.scrollWidth > node.clientWidth + 1)
  }, [])

  // A callback ref rather than an effect: the measured node unmounts while the
  // title is being edited and remounts afterwards without `value` changing on that
  // render, so an effect keyed on the value would leave the remounted node
  // unobserved and the tooltip permanently off.
  const setNode = useCallback((node) => {
    observerRef.current?.disconnect()
    observerRef.current = null
    nodeRef.current = node

    if (!node) {
      setIsTruncated(false)
      return
    }

    measure()

    if (typeof ResizeObserver === 'undefined') return

    observerRef.current = new ResizeObserver(measure)
    observerRef.current.observe(node)
  }, [measure])

  useEffect(() => () => observerRef.current?.disconnect(), [])

  // A new value can change the text width without resizing the box, which a
  // ResizeObserver would not report.
  useEffect(() => {
    measure()
  }, [measure, value])

  return [setNode, isTruncated]
}
